import { Html } from '@react-three/drei';
import React, { useMemo, useEffect, FunctionComponent, memo } from 'react';
import { useSelector } from 'react-redux';
import { IDesignerState, IDragState } from '@/../typings';
import { COLORS } from '@/utils';
import { ITuple3, IComponentType } from '@r3f-component-data-structure';
import CSS from './DragElement.module.scss';

interface IParentProps {
	position: ITuple3;
	dragState: IDragState;
}

const DragElement: FunctionComponent<IParentProps> = ({ dragState, position }) => {
	const isFaceLandmarkHovered = useSelector((state: IDesignerState) => !!state.userReducer.activeFaceLandmark);
	const opacity = isFaceLandmarkHovered ? 0.4 : 0.9;
	const classes = useMemo(() => [CSS.DragElContainer], []);
	useEffect(() => {
		setTimeout(() => classes.push(CSS.DragShadow), 5);
	}, [classes]);

	const entityObj = useMemo(() => {
		const dragEl = document.getElementById(dragState.id);
		if (!dragEl) return;
		const domRect = dragEl.getBoundingClientRect();
		const { width, height } = domRect;
		return { dragEl, width, height };
	}, [dragState.id]);
	if (!entityObj) return null;
	let { width, height } = entityObj;
	const { dragEl } = entityObj;

	if (!dragState || !document.getElementById(dragState.id)) return null;
	if (dragState.type === IComponentType.Text || dragState.type === IComponentType.Text3d || dragState.type === IComponentType.Button) {
		const entity = dragState.entity;
		switch (entity.type) {
			case IComponentType.Text: {
				return <Html position={position}>
					<div
						className={CSS.DragElement}
						style={{
							width: Math.round(width) + 8,
							height,
							opacity,
							display: dragEl.style.display,
							alignItems: dragEl.style.alignItems,
							justifyContent: dragEl.style.justifyContent,
							fontFamily: entity.fontFamily,
							color: COLORS.midnight,
							fontSize: dragEl.style.fontSize,
							borderRadius: dragEl.style.borderRadius,
							borderWidth: dragEl.style.borderWidth,
							borderColor: dragEl.style.borderColor,
							backgroundColor: 'none !important',
						}}
					>
						<span
							style={{
								color: COLORS.midnight,
								fontFamily: entity.fontFamily,
								fontSize: dragEl.style.fontSize,
							}}
						>
							{entity.text}
						</span>
					</div>
				</Html>
			}
			case IComponentType.Button: {
				return <Html position={position}>
					<div
						className={CSS.DragElement}
						style={{
							width: dragEl.style.width,
							height: dragEl.style.height,
							opacity,
							display: dragEl.style.display,
							alignItems: dragEl.style.alignItems,
							justifyContent: dragEl.style.justifyContent,
							fontFamily: dragEl.style.fontFamily,
							borderRadius: dragEl.style.borderRadius,
							borderWidth: dragEl.style.borderWidth,
							borderColor: dragEl.style.borderColor,
							backgroundColor: dragEl.style.backgroundColor,
							backgroundImage: dragEl.style.backgroundImage,
							backgroundSize: 'contain',
							backgroundPositionX: 'center',
							backgroundPositionY: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					>
						<span style={{ color: dragEl.style.color }}>{entity.svgUrl ? '' : entity.text}</span>
					</div>
				</Html>
			}
			case IComponentType.Text3d: {
				// const entityRatio = entity.scale[0] / entity.scale[1];
				// if (entityRatio > 0) height = width / entityRatio;
				//else width = height * entityRatio;
	
				// const standardTextureImageUrl = 'https://d1sonmy4sf0d9y.cloudfront.net/image/dev6-a2922d5ead744b75b385bc88fb352b9e/img'
				// const normalTextureImageUrl = 'https://d1sonmy4sf0d9y.cloudfront.net/image/dev6-3195f757844b4cff95a0730526b518b2/img'
	
				// const imgUrl = entity.material === 'standard' ? standardTextureImageUrl : normalTextureImageUrl;
				return <Html position={position}>
					<div
						className={CSS.DragElement}
						style={{
							width: Math.round(width) + 8,
							height,
							opacity,
							display: dragEl.style.display,
							alignItems: dragEl.style.alignItems,
							justifyContent: dragEl.style.justifyContent,
							fontFamily: entity.fontFamily,
							color: COLORS.midnight,
							fontSize: dragEl.style.fontSize,
							borderRadius: dragEl.style.borderRadius,
							borderWidth: dragEl.style.borderWidth,
							borderColor: dragEl.style.borderColor,
							backgroundColor: 'none !important',
						}}
					>
						<span
							style={{
								color: COLORS.midnight,
								fontFamily: entity.fontFamily,
								fontSize: dragEl.style.fontSize,
							}}
						>
							{entity.text}
						</span>
					</div>
				</Html>
			}
		}
	} else {
		const mediaFile = 'mediaFile' in dragState ? dragState.mediaFile : null;
		// An external media library asset is still being copied into the workspace, so
		// there's no thumbnail yet — drag an empty box of the card's size instead.
		if (!mediaFile) {
			return <Html className={CSS.DragOuter} position={position}>
				<div
					className={CSS.DragPlaceholder}
					style={{ width: dragState.dimensions[0], height: dragState.dimensions[1], opacity }}
				/>
			</Html>
		}

		switch (dragState.type) {
			case IComponentType.Image: {
				// const entityRatio = entity.scale[0] / entity.scale[1];
				// if (entityRatio > 0) height = width / entityRatio;
				// else width = height * entityRatio;
	
				return <Html className={CSS.DragOuter} position={position}>
					<img
						className={CSS.DragImage}
						style={{ width, height, opacity }}
						src={mediaFile.thumbnailUrl}
					/>
				</Html>
			}
			case IComponentType.Model3d: {
				return <Html className={CSS.DragOuter} position={position}>
					<img
						className={CSS.DragImage}
						style={{ width: dragState.dimensions[0], height: dragState.dimensions[1], opacity }}
						src={mediaFile.thumbnailUrl}
					/>
				</Html>
			}
			case IComponentType.Video: {
				// const entityRatio = entity.scale[0] / entity.scale[1];
				// if (entityRatio > 0) height = width / entityRatio;
				// else width = height * entityRatio;
				return <Html className={CSS.DragOuter} position={position}>
					<img
						className={CSS.DragImage}
						style={{ width: dragState.dimensions?.[0], height: dragState.dimensions?.[1], opacity }}
						src={mediaFile.thumbnailUrl}
					/>
				</Html>
			}
		}
	}
	return null
}

export default memo(DragElement)
